/**
 * The Releases tab's form logic, kept out of the screen so it can be tested
 * (RidgeWill/keyodo#709). The server re-checks everything here; these exist so
 * a problem shows while typing rather than on Save.
 *
 * Versions are MAJOR.MINOR.PATCH (Jon, 2026-10-18): major when the app changes
 * as a whole (1.0.0 is the public launch), minor for something new people can
 * see or use, patch for fixes only.
 */
import type { AppVersions, ReleaseNote, ReleaseNoteInput } from './api.js';

export const NOTES_LIMIT = 4000;

const VERSION = /^(0|[1-9]\d{0,3})\.(0|[1-9]\d{0,3})\.(0|[1-9]\d{0,3})$/;
const EM_DASH = /\u2014|&mdash;/;

export type ReleaseForm = ReleaseNoteInput;

export const EMPTY_FORM: ReleaseForm = { version: '', releasedOn: null, notes: '', significant: false, significantNote: '' };

export function formFromNote(n: ReleaseNote): ReleaseForm {
  return {
    version: n.version,
    releasedOn: n.releasedOn,
    notes: n.notes,
    significant: n.significant,
    significantNote: n.significantNote,
  };
}

/** The body to send: trimmed, with an empty date as null. */
export function bodyFromForm(f: ReleaseForm): ReleaseNoteInput {
  return {
    version: f.version.trim(),
    releasedOn: f.releasedOn ? f.releasedOn : null,
    notes: f.notes,
    significant: f.significant,
    significantNote: f.significant ? f.significantNote : '',
  };
}

export function parseVersion(v: string): [number, number, number] | null {
  const m = VERSION.exec(v.trim());
  return m ? [Number(m[1]), Number(m[2]), Number(m[3])] : null;
}

/**
 * The next version of each kind after the newest one there is, published or
 * not. `null` when there are no notes yet, so the form asks rather than guessing.
 */
export function nextVersions(notes: Pick<ReleaseNote, 'version'>[]): { patch: string; minor: string; major: string } | null {
  let newest: [number, number, number] | null = null;
  for (const n of notes) {
    const v = parseVersion(n.version);
    if (!v) continue;
    if (!newest || v[0] > newest[0] || (v[0] === newest[0] && (v[1] > newest[1] || (v[1] === newest[1] && v[2] > newest[2])))) {
      newest = v;
    }
  }
  if (!newest) return null;
  const [major, minor, patch] = newest;
  return { patch: `${major}.${minor}.${patch + 1}`, minor: `${major}.${minor + 1}.0`, major: `${major + 1}.0.0` };
}

/** Problems with the form as it stands, in words. Empty when it can be saved. */
export function formProblems(f: ReleaseForm): string[] {
  const problems: string[] = [];
  if (!parseVersion(f.version)) problems.push('The version must be MAJOR.MINOR.PATCH, like 1.4.2.');
  if (f.notes.length > NOTES_LIMIT) problems.push(`The notes can be at most ${NOTES_LIMIT.toLocaleString('en-US')} characters.`);
  if (EM_DASH.test(f.notes)) problems.push('The notes contain an em dash. Use a comma, a colon or a new sentence.');
  if (f.significant && EM_DASH.test(f.significantNote)) {
    problems.push('The note for parents contains an em dash. Use a comma, a colon or a new sentence.');
  }
  return problems;
}

/** What is still missing before a note can be published. */
export function missingForPublish(f: Pick<ReleaseForm, 'notes' | 'significant' | 'significantNote'>): string[] {
  const missing: string[] = [];
  if (!f.notes.trim()) missing.push('the notes');
  if (f.significant && !f.significantNote.trim()) missing.push('the note for parents');
  return missing;
}

export type PreviewBlock = { kind: 'paragraph' | 'bullet'; text: string };

/**
 * The notes as the app will lay them out: paragraphs, and "- " (or "* ", or
 * "1. ") lines as bullets. The same subset as the app's reader, so the preview
 * cannot promise formatting the app does not do. Inline markup is shown as
 * written.
 */
export function previewBlocks(notes: string): PreviewBlock[] {
  const blocks: PreviewBlock[] = [];
  let paragraph: string[] = [];
  const flush = () => {
    if (paragraph.length) blocks.push({ kind: 'paragraph', text: paragraph.join(' ') });
    paragraph = [];
  };
  for (const raw of notes.replace(/\r\n/g, '\n').split('\n')) {
    const line = raw.trim();
    if (!line) {
      flush();
      continue;
    }
    const bullet = /^(?:[-*+]|\d+[.)])\s+(.*)$/.exec(line);
    if (bullet) {
      flush();
      blocks.push({ kind: 'bullet', text: bullet[1]! });
      continue;
    }
    paragraph.push(line);
  }
  flush();
  return blocks;
}

/**
 * `YYYY-MM-DD` on this computer's calendar. Publish sends it, so an undated
 * note is dated the day the publisher sees, not the server's UTC day, which is
 * already tomorrow on a US evening.
 */
export function localDate(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function isDirty(f: ReleaseForm, saved: ReleaseForm): boolean {
  const a = bodyFromForm(f);
  const b = bodyFromForm(saved);
  return (Object.keys(a) as (keyof ReleaseNoteInput)[]).some((k) => a[k] !== b[k]);
}

/**
 * What Publish asks first (RidgeWill/auth#680). Publishing makes the version
 * the app's latest, so everyone older is prompted, and a required one blocks.
 */
export function publishQuestion(note: Pick<ReleaseNote, 'version' | 'required'>): string {
  const base = `Is ${note.version} live in both the App Store and Google Play? Everyone on an older version will be prompted to update.`;
  return note.required ? `${base} It is marked Required, so older versions will be blocked until they update.` : base;
}

/** What turning Required on asks first. Nothing is asked to turn it off. */
export function requiredQuestion(note: Pick<ReleaseNote, 'version' | 'published'>): string {
  return note.published
    ? `Is ${note.version} live in both the App Store and Google Play? Everyone on an older version will be blocked until they update.`
    : `When ${note.version} is published, everyone on an older version will be blocked until they update.`;
}

/** One line on how the last versions event went, for beside the Sync button. */
export function deliveryLine(v: AppVersions): { tone: 'good' | 'warn' | 'danger' | 'neutral'; text: string } {
  const d = v.delivery;
  const why = d.lastError ? ` (${d.lastError})` : '';
  switch (d.status) {
    case 'delivered':
      return { tone: 'good', text: `The app accepted revision ${d.deliveredRevision}.` };
    case 'pending':
      return { tone: 'warn', text: `The app has not accepted revision ${v.revision} yet${why}. It is retried automatically.` };
    case 'not-subscribed':
      return {
        tone: 'warn',
        text: 'Not sent: the app does not subscribe to application.versions-changed. Turn it on in its event subscriptions, then Sync.',
      };
    case 'unconfigured':
      return { tone: 'warn', text: 'Not sent: the app has no event webhook URL. Add one, then Sync.' };
    case 'abandoned':
      return { tone: 'danger', text: `Stopped retrying after ${d.attempts} attempts${why}. Fix the webhook, then Sync.` };
    default:
      return { tone: 'neutral', text: 'Nothing sent yet.' };
  }
}
